import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const nowIso = () => new Date().toISOString();

const getUserKeyFromSession = (session) => {
  if (!session) return 'anonymous';
  if (session.type === 'guest') return 'guest';
  return session?.user?.id || session?.user?.email || 'anonymous';
};

const createDefaultNotifications = (role) => [
  {
    id: `noti-important-${Date.now()}`,
    type: 'important',
    title: 'Thông báo hệ thống',
    message: 'Nền tảng vừa cập nhật chính sách bảo mật và hiệu năng.',
    actionLabel: 'Xem chính sách',
    actionType: 'link',
    createdAt: nowIso(),
    read: false,
  },
  {
    id: `noti-reminder-${Date.now() + 1}`,
    type: 'reminder',
    title: 'Nhắc nhở học tập',
    message:
      role === 'teacher'
        ? 'Bạn có 2 quiz cần công bố trong hôm nay.'
        : role === 'parent'
          ? 'Hôm nay có lịch ôn tập của con lúc 19:30.'
          : role === 'admin'
            ? 'Có 3 ticket support chưa được xử lý.'
            : 'Bạn có bài tập cần hoàn thành trước 21:00.',
    actionLabel: 'Xem chi tiết',
    actionType: 'internal',
    createdAt: nowIso(),
    read: false,
  },
  {
    id: `noti-ads-${Date.now() + 2}`,
    type: 'ads',
    title: 'Gợi ý tính năng mới',
    message: 'Khám phá gói Quiz Atelier Pro để mở thêm bộ công cụ nâng cao.',
    actionLabel: 'Khám phá',
    actionType: 'promo',
    createdAt: nowIso(),
    read: false,
  },
];

export const useUserWorkspaceStore = create(
  persist(
    (set, get) => ({
      theme: 'light',
      profileByUserKey: {},
      roleDataByUserKey: {},
      notificationsByUserKey: {},
      tickets: [],

      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),

      ensureProfile: (session) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;

        set((state) => {
          const existed = state.profileByUserKey[userKey];
          if (existed) return state;

          const role = session?.role || session?.user?.role || 'student';
          const profile = {
            userKey,
            name: session?.user?.name || 'Người dùng',
            email: session?.user?.email || '',
            passwordMask: '********',
            roleType: role,
            createdAt: nowIso(),
            updatedAt: nowIso(),
          };

          const roleData = {
            ...(state.roleDataByUserKey[userKey] || {}),
            [role]: {
              lastVisitedDashboardAt: nowIso(),
              preferences: {},
            },
          };

          return {
            ...state,
            profileByUserKey: {
              ...state.profileByUserKey,
              [userKey]: profile,
            },
            roleDataByUserKey: {
              ...state.roleDataByUserKey,
              [userKey]: roleData,
            },
          };
        });
      },

      updateProfile: (session, updates) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        set((state) => {
          const current = state.profileByUserKey[userKey] || {
            userKey,
            name: '',
            email: '',
            passwordMask: '********',
            roleType: session?.role || 'student',
            createdAt: nowIso(),
          };
          return {
            ...state,
            profileByUserKey: {
              ...state.profileByUserKey,
              [userKey]: {
                ...current,
                ...updates,
                updatedAt: nowIso(),
              },
            },
          };
        });
      },

      switchRoleType: (session, newRoleType) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        set((state) => {
          const currentProfile = state.profileByUserKey[userKey];
          const existingRoleData = state.roleDataByUserKey[userKey] || {};
          return {
            ...state,
            profileByUserKey: {
              ...state.profileByUserKey,
              [userKey]: {
                ...currentProfile,
                roleType: newRoleType,
                updatedAt: nowIso(),
              },
            },
            roleDataByUserKey: {
              ...state.roleDataByUserKey,
              [userKey]: {
                ...existingRoleData,
                [newRoleType]: existingRoleData[newRoleType] || {
                  lastVisitedDashboardAt: nowIso(),
                  preferences: {},
                },
              },
            },
          };
        });
      },

      seedNotificationsForUser: (session) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        const existing = get().notificationsByUserKey[userKey];
        if (existing && existing.length > 0) return;
        const role = session?.role || 'student';
        set((state) => ({
          ...state,
          notificationsByUserKey: {
            ...state.notificationsByUserKey,
            [userKey]: createDefaultNotifications(role),
          },
        }));
      },

      markNotificationRead: (session, notificationId) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        set((state) => ({
          ...state,
          notificationsByUserKey: {
            ...state.notificationsByUserKey,
            [userKey]: (state.notificationsByUserKey[userKey] || []).map((n) =>
              n.id === notificationId ? { ...n, read: true } : n,
            ),
          },
        }));
      },

      markAllNotificationsRead: (session) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        set((state) => ({
          ...state,
          notificationsByUserKey: {
            ...state.notificationsByUserKey,
            [userKey]: (state.notificationsByUserKey[userKey] || []).map((n) => ({
              ...n,
              read: true,
            })),
          },
        }));
      },

      deleteNotification: (session, notificationId) => {
        const userKey = getUserKeyFromSession(session);
        if (userKey === 'anonymous') return;
        set((state) => ({
          ...state,
          notificationsByUserKey: {
            ...state.notificationsByUserKey,
            [userKey]: (state.notificationsByUserKey[userKey] || []).filter(
              (n) => n.id !== notificationId,
            ),
          },
        }));
      },

      createSupportTicket: (session, payload) => {
        const userKey = getUserKeyFromSession(session);
        const ticket = {
          id: `TKT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
          userKey,
          role: session?.role || 'student',
          status: 'open',
          createdAt: nowIso(),
          ...payload,
        };
        set((state) => ({
          ...state,
          tickets: [ticket, ...state.tickets],
        }));

        set((state) => ({
          ...state,
          notificationsByUserKey: {
            ...state.notificationsByUserKey,
            [userKey]: [
              {
                id: `noti-ticket-${Date.now()}`,
                type: 'important',
                title: 'Ticket đã được gửi',
                message: `Support ticket ${ticket.id} đã tạo thành công.`,
                createdAt: nowIso(),
                read: false,
              },
              ...(state.notificationsByUserKey[userKey] || []),
            ],
          },
        }));

        return ticket;
      },
    }),
    {
      name: 'eduquiz-user-workspace',
      version: 1,
    },
  ),
);

